import { getLocale, useTranslation } from '@rhino-project/core/i18n';
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  AlertDialog,
  Button,
  Card,
  Disclosure,
  Fieldset,
  FieldsetActions,
  FieldsetLegend,
  Form,
  Typography
} from '@heroui/react';
import {
  FormErrors,
  ModelCreateSimple,
  ModelEditSimple,
  ModelFieldGroup
} from '@rhino-project/ui-heroui';
import { Icon } from '@iconify/react';
import {
  useModelCreateContext,
  useModelDelete,
  useModelEditContext,
  useModelIndex,
  useModelIndexInfinite
} from '@rhino-project/core/hooks';
import { useFormContext } from 'react-hook-form';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { getYouTubeThumbnail } from '../../../../../../utils/youtube';
import type { RhinoRead } from '@rhino-project/core';

type Video = RhinoRead<'video'>;

const FIELD_PATHS = ['title', 'url', 'category', 'scheduled_at', 'description'];

const todayIso = () => today(getLocalTimeZone()).toString();

type FormMode =
  { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; id: number };

const formFields = (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ModelFieldGroup path="title" variant="secondary" />
      <ModelFieldGroup path="url" variant="secondary" />
      <ModelFieldGroup path="scheduled_at" variant="secondary" />
    </div>
    <ModelFieldGroup path="description" variant="secondary" />
  </>
);

const VideoCreateForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelCreateContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Form>
        <Fieldset>
          <FieldsetLegend className="text-lg font-semibold">
            {t('frontend.messages.new_video')}
          </FieldsetLegend>
          <FormErrors />
          {formFields}
          <FieldsetActions>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={isPending}
              data-testid="video-save"
            >
              {t('frontend.messages.add')}
            </Button>
            <Button variant="ghost" onPress={onDone}>
              {t('frontend.messages.cancel')}
            </Button>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </Card>
  );
};

const VideoEditForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelEditContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Form>
        <Fieldset>
          <FieldsetLegend className="text-lg font-semibold">
            {t('frontend.messages.edit_video_2')}
          </FieldsetLegend>
          <FormErrors />
          {formFields}
          <FieldsetActions>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={isPending}
              data-testid="video-save"
            >
              {t('frontend.messages.save')}
            </Button>
            <Button variant="ghost" onPress={onDone}>
              {t('frontend.messages.cancel')}
            </Button>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </Card>
  );
};

type CardActions = {
  onEdit: (video: Video) => void;
  onDelete: (video: Video) => void;
};

const VideoCard = ({
  video,
  onEdit,
  onDelete
}: { video: Video } & CardActions) => {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-center gap-4">
        <img
          src={getYouTubeThumbnail(video.url)}
          alt={video.title}
          className="w-32 aspect-video object-cover rounded shrink-0"
        />
        <div className="flex-1 min-w-0">
          <Card.Title>{video.title}</Card.Title>
          {video.description && (
            <p className="text-sm text-muted">{video.description}</p>
          )}
          {video.scheduled_at && (
            <p className="text-xs text-muted mt-1">
              {parseDate(video.scheduled_at as string)
                .toDate(getLocalTimeZone())
                .toLocaleDateString(getLocale().replaceAll('_', '-'), {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('frontend.messages.edit_video')}
            onPress={() => onEdit(video)}
          >
            <Icon icon="bi:pencil" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('frontend.messages.delete_video')}
            onPress={() => onDelete(video)}
            className="text-danger"
          >
            <Icon icon="bi:trash" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const PastVideos = ({
  teamSeasonId,
  todayIso: now,
  onEdit,
  onDelete
}: {
  teamSeasonId: number;
  todayIso: string;
  onEdit: (video: Video) => void;
  onDelete: (video: Video) => void;
}) => {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);

  const { results, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useModelIndexInfinite('video', {
      filter: {
        team_season_id: teamSeasonId,
        category: 'time_based',
        scheduled_at: { lt: now }
      },
      order: ['-scheduled_at', '-created_at'],
      limit: 20,
      queryOptions: { enabled: isExpanded }
    });
  const videos = results ?? [];

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isExpanded]);

  return (
    <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
      <Disclosure.Heading>
        <Disclosure.Trigger className="flex items-center gap-2 w-full text-left font-semibold">
          {t('frontend.messages.past_prep')}
          <Disclosure.Indicator />
        </Disclosure.Trigger>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body>
          {isExpanded && videos.length === 0 && !isFetchingNextPage ? (
            <p className="text-muted py-2">
              {t('frontend.messages.no_past_prep')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {videos.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
              {hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex justify-center py-4 text-sm text-muted"
                >
                  {isFetchingNextPage
                    ? t('frontend.messages.loading_more')
                    : ''}
                </div>
              )}
            </div>
          )}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  );
};

const AdminUpcomingVideos = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false }) as {
    team_season_id: string;
  };
  const teamSeasonId = Number(team_season_id);
  const now = todayIso();

  const { results: currentResults } = useModelIndex('video', {
    filter: {
      team_season_id: teamSeasonId,
      category: 'time_based',
      scheduled_at: { gteq_coalesce: now }
    },
    order: ['scheduled_at', '-created_at'],
    limit: 100
  });
  const currentVideos = currentResults ?? [];

  const { mutate: deleteVideo } = useModelDelete('video');

  const [mode, setMode] = useState<FormMode>({ kind: 'none' });
  const [deleting, setDeleting] = useState<Video | null>(null);

  const closeForm = () => setMode({ kind: 'none' });
  const handleEdit = (video: Video) => setMode({ kind: 'edit', id: video.id });

  const handleConfirmDelete = () => {
    if (!deleting) return;
    deleteVideo({ id: deleting.id } as never, {
      onSuccess: () => setDeleting(null)
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Typography.Heading level={1}>
          {t('frontend.messages.prep_videos')}
        </Typography.Heading>
        <Button variant="primary" onPress={() => setMode({ kind: 'create' })}>
          <Icon icon="bi:plus" className="mr-1" />
          {t('frontend.messages.add_video')}
        </Button>
      </div>

      {mode.kind === 'create' && (
        <ModelCreateSimple
          model="video"
          parentId={teamSeasonId}
          paths={FIELD_PATHS as never}
          extraDefaultValues={{ category: 'time_based' }}
          fallback={false}
        >
          <VideoCreateForm onDone={closeForm} />
        </ModelCreateSimple>
      )}

      {mode.kind === 'edit' && (
        <ModelEditSimple
          model="video"
          modelId={mode.id}
          paths={FIELD_PATHS as never}
          fallback={false}
        >
          <VideoEditForm onDone={closeForm} />
        </ModelEditSimple>
      )}

      <div className="flex flex-col gap-6">
        {currentVideos.length === 0 ? (
          <p className="text-muted">
            {t('frontend.messages.no_prep_scheduled')}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {currentVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onEdit={handleEdit}
                onDelete={setDeleting}
              />
            ))}
          </div>
        )}

        <PastVideos
          teamSeasonId={teamSeasonId}
          todayIso={now}
          onEdit={handleEdit}
          onDelete={setDeleting}
        />
      </div>

      <AlertDialog
        isOpen={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>
                  {t('frontend.messages.delete_video_2')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {t('frontend.messages.delete_confirmation', {
                  name: deleting?.title as string
                })}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={() => setDeleting(null)}>
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onPress={handleConfirmDelete}
                  data-testid="video-delete-confirm"
                >
                  {t('frontend.messages.delete')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
};

export const Route = createFileRoute(
  '/_authenticated/$owner/active/$team_season_id/videos/'
)({
  component: AdminUpcomingVideos
});
